import * as Sentry from "@sentry/react";

class Monitoring {
  static isInitialized = false;
  static isProduction = process.env.NODE_ENV === 'production';

  // Initialize monitoring (call this in your main App component)
  static init() {
    if (this.isInitialized || !this.isProduction) return;

    try {
      Sentry.init({
        dsn: "https://88fe01a2ae5f84f276ed98034c5b5ec9@o4509539066773504.ingest.us.sentry.io/4509539069263872",
        environment: process.env.NODE_ENV,
        tracesSampleRate: 0.1, // Capture 10% of transactions for performance monitoring
      beforeSend(event) {
        // Filter out non-critical errors
        if (event.exception) {
          const error = event.exception.values[0];
            if (error && error.value && (
              error.value.includes('Network Error') ||
              error.value.includes('Failed to fetch') ||
              error.value.includes('net::ERR_FAILED') ||
              error.value.includes('ServiceWorker')
            )) {
              return null; // Don't send network/service worker errors
          }
        }
        return event;
        },
        // Add network error filtering for transport
        transport: {
          shouldAttempt: () => {
            try {
              return navigator.onLine;
            } catch {
              return true;
            }
          }
      }
    });

      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize Sentry:', error);
      // Continue without monitoring rather than crash the app
    }
  }

  // Set user context (call when user logs in)
  static setUser(user) {
    if (!this.isProduction) return;
    
    Sentry.setUser({
      id: user.uid,
      email: user.email,
      username: user.displayName
    });
  }

  // Clear user context (call when user logs out)
  static clearUser() {
    if (!this.isProduction) return;
    Sentry.setUser(null);
  }

  // Log errors manually
  static logError(error, context = {}) {
    if (!this.isProduction) {
      console.error('Error:', error, context);
      return;
    }

    Sentry.withScope((scope) => {
      Object.keys(context).forEach(key => {
        scope.setTag(key, context[key]);
      });
      Sentry.captureException(error);
    });
  }

  // Log custom messages
  static logMessage(message, level = 'info', extra = {}) {
    if (!this.isProduction) {
      console.log(`[${level.toUpperCase()}] ${message}`, extra);
      return;
    }

    Sentry.withScope((scope) => {
      scope.setLevel(level);
      Object.keys(extra).forEach(key => {
        scope.setExtra(key, extra[key]);
      });
      Sentry.captureMessage(message);
    });
  }

  // Add breadcrumb for debugging
  static addBreadcrumb(message, category = 'custom', level = 'info', data = {}) {
    if (!this.isProduction) return;

    Sentry.addBreadcrumb({
      message,
      category,
      level,
      data
    });
  }

  // Performance monitoring (simplified for compatibility)
  static startTransaction(name, op = 'navigation') {
    if (!this.isProduction) return null;
    // For now, just add a breadcrumb instead of full transaction tracking
    this.addBreadcrumb(`Starting ${name}`, 'performance', 'info', { operation: op });
    return { name, op, startTime: performance.now() };
  }

  static finishTransaction(transaction) {
    if (!this.isProduction || !transaction) return;
    const duration = performance.now() - transaction.startTime;
    this.addBreadcrumb(`Finished ${transaction.name}`, 'performance', 'info', { 
      duration: Math.round(duration),
      operation: transaction.op 
    });
  }
}

export default Monitoring; 